import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/**
 * VisitorMap — plots visitor lat/lng dots on an OpenStreetMap background.
 * Uses vanilla Leaflet (not react-leaflet) because react-leaflet trips React 18
 * Strict Mode's double-invoke with "Map container is already initialized".
 * Here we own the lifecycle explicitly via useRef, so every mount is safe.
 *
 * Circle-marker radius scales with view count so busy cities stand out.
 */
export default function VisitorMap({ points = [] }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  // Bootstrap the Leaflet map exactly once per container.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: [54.5, -3.5], // UK default
      zoom: 5,
      scrollWheelZoom: false,
      zoomControl: true,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 18,
    }).addTo(map);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);

    return () => {
      try { map.remove(); } catch (e) { /* no-op */ }
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  // Update markers + recenter whenever points change.
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();

    if (!points.length) return;

    const maxViews = Math.max(1, ...points.map((p) => p.views || 0));
    const latLngs = [];

    points.forEach((p) => {
      if (typeof p.lat !== "number" || typeof p.lng !== "number") return;
      const scale = Math.sqrt((p.views || 1) / maxViews);
      const radius = 6 + scale * 16;
      const marker = L.circleMarker([p.lat, p.lng], {
        radius,
        color: "#E26D29",
        fillColor: "#E26D29",
        fillOpacity: 0.55,
        weight: 1.5,
      });
      const cityLine = `${p.city || "Unknown city"}${p.region && p.region !== p.city ? `, ${p.region}` : ""}`;
      marker.bindPopup(
        `<div style="font-family:Inter,system-ui,sans-serif">
           <div style="font-weight:700;color:#11523B">${escapeHtml(cityLine)}</div>
           <div style="color:#4A5D54;font-size:12px;margin-top:2px">${escapeHtml(p.country_name || p.country_code || "")}</div>
           <div style="margin-top:6px;font-size:12px"><strong>${p.views}</strong> view${p.views === 1 ? "" : "s"} · <strong>${p.uniques}</strong> unique</div>
         </div>`
      );
      marker.addTo(layer);
      latLngs.push([p.lat, p.lng]);
    });

    if (latLngs.length) {
      const inUK = points.filter((p) => (p.country_code || "").toUpperCase() === "GB");
      if (inUK.length && inUK.length === points.length && latLngs.length > 1) {
        map.fitBounds(latLngs, { padding: [30, 30], maxZoom: 8 });
      } else if (latLngs.length === 1) {
        map.setView(latLngs[0], 8);
      } else {
        map.fitBounds(latLngs, { padding: [30, 30], maxZoom: 6 });
      }
    }
  }, [points]);

  return (
    <div className="brand-card p-0 overflow-hidden" data-testid="visitor-map-card">
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        <div className="font-display font-extrabold text-base" style={{ color: "var(--brand-green)" }}>
          Where your visitors are (7d)
        </div>
        <div className="text-xs text-[var(--text-muted)]">
          {points.length} location{points.length === 1 ? "" : "s"}
        </div>
      </div>

      {points.length === 0 && (
        <div className="px-5 pb-5 text-sm text-[var(--text-muted)]" data-testid="visitor-map-empty">
          No mapped visits yet — the moment real traffic lands, dots will appear here.
        </div>
      )}

      <div
        ref={containerRef}
        data-testid="visitor-map"
        style={{ height: points.length === 0 ? 0 : 380, width: "100%" }}
      />
    </div>
  );
}

function escapeHtml(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
